import React, { useState } from "react";
import {
  useCreateHttpConfig,
  useDeleteHttpConfig,
  useHttpConfigs,
  useUpdateHttpConfig,
} from "../../hooks/useHttp";
import LoadingSpinner from "../../components/ui/LoadingSpinner";
import ErrorMessage from "../../components/ui/ErrorMessage";
import { Input, Textarea } from "../../components/ui/Input";
import { useNavigate } from "react-router-dom";

interface ConfigFormData {
  name: string;
  base_url: string;
  description?: string;
  headers?: string;
  auth_type?: string;
  auth_credentials?: string;
  timeout_seconds?: number;
  retry_count?: number;
}

export default function HttpConfigsPage() {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState<ConfigFormData>({
    name: "",
    base_url: "",
    description: "",
    headers: "{}",
    auth_type: "",
    auth_credentials: "{}",
    timeout_seconds: 30,
    retry_count: 3,
  });
  const navigate = useNavigate();

  const { data, isLoading, isError, refetch } = useHttpConfigs({
    page,
    page_size: 12,
    search: search || undefined,
  });
  const createMutation = useCreateHttpConfig();
  const updateMutation = useUpdateHttpConfig();
  const deleteMutation = useDeleteHttpConfig();

  const items = data?.configs || [];
  const totalPages = data?.total_pages || 1;

  const openCreate = () => {
    setEditingId(null);
    setFormData({
      name: "",
      base_url: "",
      description: "",
      headers: "{}",
      auth_type: "",
      auth_credentials: "{}",
      timeout_seconds: 30,
      retry_count: 3,
    });
    setShowForm(true);
  };

  const openEdit = (row: any) => {
    navigate(`/http-configs/${row.id}/edit`);
  };

  const submitForm = async (e: React.FormEvent) => {
    e.preventDefault();
    const payload = {
      name: formData.name,
      base_url: formData.base_url,
      description: formData.description || undefined,
      headers: safeJson(formData.headers),
      auth_type: formData.auth_type || undefined,
      auth_credentials: safeJson(formData.auth_credentials),
      timeout_seconds: formData.timeout_seconds ?? undefined,
      retry_count: formData.retry_count ?? undefined,
    };
    if (editingId) {
      await updateMutation.mutateAsync({ id: editingId, data: payload });
    } else {
      await createMutation.mutateAsync(payload);
    }
    setShowForm(false);
    refetch();
  };

  const onDelete = async (id: number) => {
    await deleteMutation.mutateAsync(id);
    refetch();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">HTTP Configs</h1>
          <p className="text-gray-600 mt-1">
            Manage base configurations for HTTP requests
          </p>
        </div>
        <button
          onClick={openCreate}
          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
        >
          Create Config
        </button>
      </div>

      <div className="bg-white p-4 rounded-lg border border-gray-200 flex items-center gap-3">
        <Input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name or description"
          variant="default"
          className="flex-1"
          showLabel={false}
        />
        <button
          onClick={() => setPage(1)}
          className="px-3 py-2 bg-gray-100 rounded border"
        >
          Apply
        </button>
        <button
          onClick={() => {
            setSearch("");
            setPage(1);
          }}
          className="px-3 py-2 bg-gray-100 rounded border"
        >
          Clear
        </button>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      )}
      {isError && (
        <ErrorMessage
          message="Failed to load configs"
          onRetry={() => refetch()}
        />
      )}

      {!isLoading && !isError && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((row: any) => (
            <div key={row.id} className="bg-white border rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold">{row.name}</h3>
                <span className="text-xs text-gray-500">#{row.id}</span>
              </div>
              <div className="text-sm text-gray-600 mb-2">
                Base URL:{" "}
                <span className="font-mono break-all">{row.base_url}</span>
              </div>
              {row.description && (
                <div className="text-sm text-gray-700 mb-3">
                  {row.description}
                </div>
              )}
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => openEdit(row)}
                  className="px-3 py-1 text-sm bg-white border rounded"
                >
                  Edit
                </button>
                <button
                  onClick={() => onDelete(row.id)}
                  className="px-3 py-1 text-sm bg-red-500 text-white rounded"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {!isLoading && !isError && (data?.total_pages || 0) > 1 && (
        <div className="flex items-center justify-center space-x-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-3 py-2 bg-white border rounded disabled:opacity-50"
          >
            Previous
          </button>
          <span className="text-sm">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => p + 1)}
            disabled={page === totalPages}
            className="px-3 py-2 bg-white border rounded disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-lg w-full">
            <div className="p-6">
              <h2 className="text-xl font-semibold mb-4">
                {editingId ? "Edit Config" : "Create Config"}
              </h2>
              <form onSubmit={submitForm} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    className="w-full px-3 py-2 border rounded"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Base URL
                  </label>
                  <input
                    type="url"
                    value={formData.base_url}
                    onChange={(e) =>
                      setFormData({ ...formData, base_url: e.target.value })
                    }
                    className="w-full px-3 py-2 border rounded"
                    required
                  />
                </div>
                <div>
                  <Textarea
                    label="Description"
                    value={formData.description || ""}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                    variant="rounded-md"
                    rows={3}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Textarea
                      label="Headers (JSON)"
                      value={formData.headers}
                      onChange={(e) =>
                        setFormData({ ...formData, headers: e.target.value })
                      }
                      variant="rounded-md"
                      className="font-mono"
                      rows={3}
                    />
                  </div>
                  <div>
                    <Textarea
                      label="Auth Credentials (JSON)"
                      value={formData.auth_credentials}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          auth_credentials: e.target.value,
                        })
                      }
                      variant="rounded-md"
                      className="font-mono"
                      rows={3}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Auth Type
                    </label>
                    <input
                      type="text"
                      value={formData.auth_type || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, auth_type: e.target.value })
                      }
                      className="w-full px-3 py-2 border rounded"
                      placeholder="basic, bearer"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Timeout (seconds)
                    </label>
                    <input
                      type="number"
                      min={0}
                      value={formData.timeout_seconds ?? 30}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          timeout_seconds: e.target.value
                            ? Number(e.target.value)
                            : undefined,
                        })
                      }
                      className="w-full px-3 py-2 border rounded"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Retry Count
                  </label>
                  <input
                    type="number"
                    min={0}
                    value={formData.retry_count ?? 3}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        retry_count: e.target.value
                          ? Number(e.target.value)
                          : undefined,
                      })
                    }
                    className="w-full px-3 py-2 border rounded"
                  />
                </div>
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setShowForm(false)}
                    className="px-4 py-2 bg-gray-100 border rounded"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-500 text-white rounded"
                  >
                    {editingId ? "Save Changes" : "Create"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function safeJson(text?: string) {
  if (!text) return undefined;
  try {
    const obj = JSON.parse(text);
    return obj;
  } catch {
    return undefined;
  }
}
